import { type QueryFunction, useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { useMemo, useRef } from 'react';
import { z } from 'zod';
import axiosInstance from '../../../constants/axiosInstance';
import { useIntersectionObserver } from '../../../utilities/customHooks/useIntersectionObserver';

interface Asset {
  _id: string;
  plantName: string;
}

const servedPlantSchema = z.object({ _id: z.string(), plantName: z.string().optional() });
const servedPlantsSchema = z.union([z.string(), servedPlantSchema, z.array(servedPlantSchema)]).nullish();

export type ServedPlants = z.infer<typeof servedPlantsSchema>;

const plantIdsOf = (served: ServedPlants): readonly string[] => {
  const refs = Array.isArray(served) ? served : served ? [served] : [];
  return refs.map((ref) => (typeof ref === 'string' ? ref : ref._id));
};

export interface DataLogger {
  _id: string;
  name: string;
  serialNumber: string;
  createdBy: string;
  version: number;
  debug: boolean;
  plcBrand: string;
  pollingInterval: number;
  plcModel: string;
  assetId: ServedPlants;
  plantIds: readonly string[];
  asset?: Asset | Asset[];
  lastValueTime?: string;
  modbusHost: string;
  modbusPort: number;
  unitIdReal: number;
  startAddressReal: number;
  registerCountReal: number;
  unitIdBool: number;
  startAddressBool: number;
  registerCountBool: number;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

type WireDataLogger = Omit<DataLogger, 'assetId' | 'plantIds'> & { assetId: unknown };

export interface DataLoggerResponse {
  status: boolean;
  data: {
    totalPages: number;
    currentPage: number;
    dataLoggers: WireDataLogger[];
  };
}

const withServedPlants = (logger: WireDataLogger): DataLogger => {
  const assetId = servedPlantsSchema.parse(logger.assetId);
  return { ...logger, assetId, plantIds: plantIdsOf(assetId) };
};

interface FetchResponse {
  status: boolean;
  totalPages: number;
  currentPage: number;
  dataLoggers: DataLogger[];
}

const url = (page: number, name: string, limit = 8): string =>
  `/data-logger/getDataLoggers?search=${name}&pageNumber=${page}&limit=${limit}`;

const fetchDataLoggers: QueryFunction<FetchResponse, [string, string], number> = async ({
  pageParam = 1,
  queryKey,
}) => {
  const [_key, searchString] = queryKey;

  const { data } = await axiosInstance().post<DataLoggerResponse>(url(pageParam, searchString));

  return {
    status: data.status,
    totalPages: data.data.totalPages,
    currentPage: data.data.currentPage,
    dataLoggers: data.data.dataLoggers.map(withServedPlants),
  };
};

const nextPageParam = (lastPage: FetchResponse) => {
  if (lastPage.currentPage < lastPage.totalPages) {
    return lastPage.currentPage + 1;
  }
  return undefined;
};

export const useDataLoggerTable = (searchString: string) => {
  const lastElementRef = useRef<HTMLTableRowElement | null>(null);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  const { data, error, isError, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useSuspenseInfiniteQuery({
      queryKey: ['dataLoggers', searchString] as [string, string],
      queryFn: fetchDataLoggers,
      getNextPageParam: nextPageParam,
      initialPageParam: 1,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });

  const dataLoggers = useMemo(() => data.pages.flatMap((page) => page.dataLoggers), [data]);

  useIntersectionObserver(
    tableContainerRef,
    lastElementRef,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  );

  return {
    dataLoggers,
    lastElementRef,
    tableContainerRef,
    error,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  };
}; 